import axios, { AxiosInstance } from 'axios';
import type { ToastManager } from './toast.utils';

export interface ApiResponse<T> {
  status: number;
  data: T | null;
  error: any;
}

export type ApiClientOptions = {
  baseUrl: string;
  store: any;
  getToken: () => Promise<string>;
  appId: string;
  redirect?: (path: string, method: 'goTo' | 'navigate') => void;
  toast?: ToastManager;
};

export class ApiClient {
  private instance: AxiosInstance;

  constructor(options: ApiClientOptions | undefined) {
    if (!options) {
      this.instance = axios.create({
        baseURL: '',
      });
      return;
    }

    const { baseUrl, store, getToken, redirect, appId, toast } = options;

    this.instance = axios.create({
      baseURL: `${baseUrl}/${store.id}`,
    });

    this.instance.interceptors.request.use(async (request) => {
      const token = await getToken();
      request.headers = {
        ...request.headers,
        Authorization: `Bearer ${token}`,
        'Store-Country': `${store.country}`,
        'x-store-id': `${store.id}`,
      } as any;
      return request;
    });

    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const code = error?.response?.data?.error?.code || '';
        if (typeof redirect === 'function') {
          if (
            [
              'APP_NOT_INSTALLED',
              'STORE_APP_NOT_FOUND',
              'STORE_NOT_FOUND',
            ].includes(code)
          ) {
            redirect(`/apps/${appId}/authorize`, 'goTo');
          } else if (
            ['APP_NOT_PAID', 'STORE_PROFILE_INCOMPLETE'].includes(code)
          ) {
            if (toast) {
              toast.errorAction(
                code === 'APP_NOT_PAID' ? 'notPaid' : 'profileIncomplete',
              );
            }

            redirect('/profile', 'navigate');
            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
        } else {
          console.warn(error, `Error in ApiClient: ${code}`);
        }

        return Promise.reject(error);
      },
    );
  }

  async get<T>(url: string): Promise<ApiResponse<T>> {
    return this.handle<T>(() => this.instance.get<T>(url));
  }

  async post<T>(url: string, data: object): Promise<ApiResponse<T>> {
    return this.handle<T>(() => this.instance.post<T>(url, data));
  }

  async put<T>(url: string, data: object): Promise<ApiResponse<T>> {
    return this.handle<T>(() => this.instance.put<T>(url, data));
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    return this.handle<T>(() => this.instance.delete<T>(url));
  }

  private async handle<T>(
    requestFn: () => Promise<{ status: number; data: T }>,
  ): Promise<ApiResponse<T>> {
    try {
      const response = await requestFn();
      return { status: response.status, data: response.data, error: null };
    } catch (error: any) {
      return {
        status: error?.response?.status || 500,
        data: null,
        error: error?.response?.data || error.message,
      };
    }
  }
}
